import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import "./ClientList.css";

const STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "pending", label: "Pending" },
    { key: "declined", label: "Declined" },
    { key: "archived", label: "Archived" },
];

const BLANK_FORM = {
    name: "",
    email: "",
    company: "",
    preferred_language: "en",
};

const ClientList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [clients, setClients] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [statusTab, setStatusTab] = useState("all");
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ ...BLANK_FORM });
    const [creating, setCreating] = useState(false);
    const [createResult, setCreateResult] = useState(null);
    const [createError, setCreateError] = useState("");

    const [actionLoading, setActionLoading] = useState(null);

    const loadClients = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.allClients({ status: statusTab, search }),
            api.clientCounts(),
        ])
            .then(([cRes, countRes]) => {
                setClients(cRes.data || []);
                setCounts(countRes.counts || {});
            })
            .finally(() => setLoading(false));
    }, [statusTab, search]);

    useEffect(() => {
        loadClients();
    }, [loadClients]);

    // Debounce search input
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput), 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    const handleApprove = async (client) => {
        setActionLoading(client.id + "-approve");
        try {
            await api.approveClient(client.id);
            loadClients();
        } catch (err) {
            alert(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDecline = async (client) => {
        setActionLoading(client.id + "-decline");
        try {
            await api.declineClient(client.id);
            loadClients();
        } catch (err) {
            alert(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleArchive = async (client) => {
        if (
            !window.confirm(
                `Archive ${client.name}? They will lose portal access but their data is preserved.`,
            )
        )
            return;
        setActionLoading(client.id + "-archive");
        try {
            await api.archiveClient(client.id);
            loadClients();
        } catch (err) {
            alert(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (client) => {
        if (
            !window.confirm(
                `Permanently delete ${client.name}? This cannot be undone and will remove all their quotes, invoices, and projects.`,
            )
        )
            return;
        if (
            !window.confirm(
                `Are you sure? Type OK to confirm deletion of ${client.name}.`,
            )
        )
            return;
        setActionLoading(client.id + "-delete");
        try {
            await api.deleteClient(client.id);
            setClients((prev) => prev.filter((c) => c.id !== client.id));
        } catch (err) {
            alert(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateChange = (e) =>
        setCreateForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);
        setCreateError("");
        setCreateResult(null);
        try {
            const res = await api.createClient(createForm);
            setCreateResult(res);
            setCreateForm({ ...BLANK_FORM });
            loadClients();
        } catch (err) {
            setCreateError(err.message);
        } finally {
            setCreating(false);
        }
    };

    const pendingCount = counts["pending"] || 0;

    return (
        <div className="client-list">
            {/* Page header */}
            <div className="client-list__header">
                <div>
                    <h2>Clients</h2>
                    {pendingCount > 0 && (
                        <p className="client-list__pending-notice">
                            {pendingCount} client{pendingCount !== 1 ? "s" : ""}{" "}
                            awaiting approval
                        </p>
                    )}
                </div>
                <button
                    className="btn btn--primary btn--sm"
                    onClick={() => {
                        setShowCreate((v) => !v);
                        setCreateResult(null);
                        setCreateError("");
                    }}
                >
                    {showCreate ? "Cancel" : "+ New Client"}
                </button>
            </div>

            {/* Inline create form */}
            {showCreate && (
                <div className="client-list__create card">
                    <h3>Create Client Account</h3>
                    {createResult && (
                        <div className="client-list__create-result">
                            <p>
                                Account created for{" "}
                                <strong>{createResult.client.name}</strong>
                            </p>
                            <p>
                                Temporary password:{" "}
                                <code>{createResult.temporary_password}</code>
                            </p>
                            <p className="client-list__create-hint">
                                Share this with the client — they should change
                                it on first login.
                            </p>
                            <button
                                className="btn btn--ghost btn--sm"
                                onClick={() =>
                                    navigate(
                                        `/admin/clients/${createResult.client.id}`,
                                    )
                                }
                            >
                                View Client Profile →
                            </button>
                        </div>
                    )}
                    <form
                        onSubmit={handleCreateSubmit}
                        className="client-list__create-form"
                    >
                        <div className="client-list__create-grid">
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    name="name"
                                    value={createForm.name}
                                    onChange={handleCreateChange}
                                    required
                                    placeholder="Jane Smith"
                                />
                            </div>
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={createForm.email}
                                    onChange={handleCreateChange}
                                    required
                                    placeholder="jane@company.com"
                                />
                            </div>
                            <div className="form-group">
                                <label>Company</label>
                                <input
                                    name="company"
                                    value={createForm.company}
                                    onChange={handleCreateChange}
                                    placeholder="Acme Inc."
                                />
                            </div>
                            <div className="form-group">
                                <label>Language</label>
                                <select
                                    name="preferred_language"
                                    value={createForm.preferred_language}
                                    onChange={handleCreateChange}
                                >
                                    <option value="en">English</option>
                                    <option value="fr">French</option>
                                    <option value="zh">Mandarin</option>
                                </select>
                            </div>
                        </div>
                        {createError && (
                            <p className="error-message">{createError}</p>
                        )}
                        <button
                            type="submit"
                            className="btn btn--primary btn--sm"
                            disabled={creating}
                        >
                            {creating ? "Creating…" : "Create Account"}
                        </button>
                    </form>
                </div>
            )}

            {/* Toolbar: status tabs + search */}
            <div className="client-list__toolbar">
                <div className="client-list__tabs">
                    {STATUS_TABS.map((tab) => {
                        const cnt =
                            tab.key === "all"
                                ? Object.values(counts).reduce(
                                      (a, b) => a + Number(b),
                                      0,
                                  )
                                : counts[tab.key] || 0;
                        return (
                            <button
                                key={tab.key}
                                className={`client-list__tab ${statusTab === tab.key ? "client-list__tab--active" : ""}`}
                                onClick={() => setStatusTab(tab.key)}
                            >
                                {tab.label}
                                {cnt > 0 && (
                                    <span
                                        className={`client-list__tab-count ${tab.key === "pending" ? "client-list__tab-count--pending" : ""}`}
                                    >
                                        {cnt}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <div className="client-list__search">
                    <input
                        type="search"
                        placeholder="Search name, email, company…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="client-list__table-wrap card">
                {loading ? (
                    <p className="client-list__loading">
                        {t("common.loading")}
                    </p>
                ) : (
                    <table className="dash-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Company</th>
                                <th>Status</th>
                                <th>Lang</th>
                                <th>Added</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="dash-table__empty"
                                    >
                                        No clients found.
                                    </td>
                                </tr>
                            )}
                            {clients.map((client) => (
                                <tr
                                    key={client.id}
                                    className={`client-list__row ${client.account_status === "pending" ? "client-list__row--pending" : ""} ${client.account_status === "archived" ? "client-list__row--archived" : ""}`}
                                >
                                    <td>
                                        <button
                                            className="client-list__name-btn"
                                            onClick={() =>
                                                navigate(
                                                    `/admin/clients/${client.id}`,
                                                )
                                            }
                                        >
                                            {client.name}
                                        </button>
                                    </td>
                                    <td className="dash-table__muted">
                                        {client.email}
                                    </td>
                                    <td className="dash-table__muted">
                                        {client.company || "—"}
                                    </td>
                                    <td>
                                        <StatusBadge
                                            status={client.account_status}
                                        />
                                    </td>
                                    <td className="dash-table__muted">
                                        {client.preferred_language?.toUpperCase()}
                                    </td>
                                    <td className="dash-table__muted">
                                        {new Date(
                                            client.created_at,
                                        ).toLocaleDateString("en-CA")}
                                    </td>
                                    <td>
                                        <div className="client-list__row-actions">
                                            {client.account_status ===
                                                "pending" && (
                                                <>
                                                    <button
                                                        className="btn btn--primary btn--xs"
                                                        disabled={
                                                            actionLoading ===
                                                            client.id +
                                                                "-approve"
                                                        }
                                                        onClick={() =>
                                                            handleApprove(
                                                                client,
                                                            )
                                                        }
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="btn btn--danger btn--xs"
                                                        disabled={
                                                            actionLoading ===
                                                            client.id +
                                                                "-decline"
                                                        }
                                                        onClick={() =>
                                                            handleDecline(
                                                                client,
                                                            )
                                                        }
                                                    >
                                                        Decline
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                className="btn btn--ghost btn--xs"
                                                onClick={() =>
                                                    navigate(
                                                        `/admin/clients/${client.id}`,
                                                    )
                                                }
                                            >
                                                View
                                            </button>
                                            {client.account_status !==
                                                "archived" && (
                                                <button
                                                    className="btn btn--ghost btn--xs"
                                                    disabled={
                                                        actionLoading ===
                                                        client.id + "-archive"
                                                    }
                                                    onClick={() =>
                                                        handleArchive(client)
                                                    }
                                                    title="Archive — disables login, keeps data"
                                                >
                                                    Archive
                                                </button>
                                            )}
                                            <button
                                                className="btn btn--danger btn--xs"
                                                disabled={
                                                    actionLoading ===
                                                    client.id + "-delete"
                                                }
                                                onClick={() =>
                                                    handleDelete(client)
                                                }
                                                title="Permanently delete"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ClientList;
